import { Component } from 'react';
import {
  hasAttemptedChunkReload,
  markChunkReloadAttempted,
  clearChunkReloadFlag,
} from '../../utils/chunkReload';

// Detects the classic "old tab, new deploy" chunk-load failure.
// After Vercel pushes a new build, the HTML the user has cached still
// references the OLD hashed chunk filenames — which no longer exist.
// React's lazy() then throws with one of these messages when the user
// clicks a route.
function isChunkLoadError(error) {
  if (!error) return false;
  const name = error.name || '';
  const msg = error.message || '';
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk \d+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    // Safari serves the SPA-fallback index.html for a missing chunk and then
    // refuses to execute it as a module — a distinct message from Chrome's.
    /is not a valid JavaScript MIME type/i.test(msg) ||
    /expected a JavaScript(-or-Wasm)? module/i.test(msg) ||
    // Firefox blocks the module with X-Content-Type-Options: nosniff and
    // reports it as a MIME type mismatch (e.g. 'Blocked due to MIME type
    // ("text/html") mismatch').
    /MIME type.*mismatch/i.test(msg)
  );
}

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // If it's a chunk-load error and we haven't already tried to recover,
    // do a hard reload so the browser fetches the new index.html with the
    // correct chunk filenames. The guard (utils/chunkReload) wraps every
    // sessionStorage access — an unguarded SecurityError here would crash the
    // top-level boundary into a white screen — and falls back to window.name so
    // a genuinely-broken deploy can't loop even where storage is disabled.
    if (isChunkLoadError(error)) {
      if (!hasAttemptedChunkReload()) {
        markChunkReloadAttempted();
        window.location.reload();
        return;
      }
    }
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info);
  }

  handleReload = () => {
    clearChunkReloadFlag();
    window.location.reload();
  };

  handleHome = () => {
    clearChunkReloadFlag();
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      const chunk = isChunkLoadError(this.state.error);
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div className="text-center max-w-md">
            <div className="text-6xl mb-4">🐾</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {chunk ? 'Aplicația a fost actualizată' : 'A apărut o eroare'}
            </h1>
            <p className="text-gray-600 mb-6">
              {chunk
                ? 'Reîncarcă pagina pentru a folosi cea mai nouă versiune.'
                : 'Ne pare rău, ceva nu a funcționat cum trebuie. Încearcă să reîncarci pagina.'}
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={this.handleReload}
                className="px-6 py-3 bg-orange-600 text-white rounded-xl font-medium hover:bg-orange-700 transition-colors"
              >
                Reîncarcă pagina
              </button>
              <button
                onClick={this.handleHome}
                className="px-6 py-3 bg-white text-gray-700 border border-gray-200 rounded-xl font-medium hover:bg-gray-50 transition-colors"
              >
                Înapoi acasă
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
